// Formatea números grandes a notación abreviada (K, M, B, T)
function formatNumberAbbr(num) {
  if (num === null || num === undefined || isNaN(num)) return '-';
  const absNum = Math.abs(Number(num));
  if (absNum >= 1e12) return (num / 1e12).toFixed(2).replace(/\.00$/, '') + 'T';
  if (absNum >= 1e9) return (num / 1e9).toFixed(2).replace(/\.00$/, '') + 'B';
  if (absNum >= 1e6) return (num / 1e6).toFixed(2).replace(/\.00$/, '') + 'M';
  if (absNum >= 1e3) return (num / 1e3).toFixed(2).replace(/\.00$/, '') + 'K';
  return num.toString();
}
import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchSensor,
  fetchBuscarSensor,
  fetchSensorById,
} from "../../actions/sensor";
import { cambiarEstadoSensor } from "../../actions/dispositivos";
import { useModuloPermissions } from "../../hooks/useModuloPermissions";
import { parserSensores } from "../../utils/parsers/sensor";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Typography,
  TextField,
  CircularProgress,
  Pagination,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SecurityIcon from "@mui/icons-material/Security";
import { usePaginationMUI } from "../../hooks/usePaginationMUI";
import Swal from "sweetalert2";
import InfoElemento from "./info-sensor";
import DialogCustom from "../../components/dialog";
import DeviceControlButton from "../../components/device-control-button";

const iconMap = {
  Voltaje: <BoltIcon fontSize="large" />,
  "Rango Mínimo": <SpeedIcon fontSize="large" />,
  "Rango Máximo": <SpeedIcon fontSize="large" />,
  Trama: <LocationOnIcon fontSize="large" />,
};

const Sensor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const permissions = useModuloPermissions('sensor');

  // Debug: log permissions
  console.log('Sensor permissions:', permissions);

  const [filter, setFilter] = useState("");
  const [debouncedFilter, setDebouncedFilter] = useState("");
  const [isFiltering, setIsFiltering] = useState(false);
  const [perPage, setPerPage] = useState(9);

  const [sensorActual, setSensorActual] = useState(null);
  // Estado para el diálogo

  const [openDialog, setOpenDialog] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Opciones de perPage - múltiplos de 3 para mejor distribución en grilla de 3 columnas
  const perPageOptions = [3, 6, 9, 12, 15, 18, 21, 24, 27, 30];

  // Debounce
  useEffect(() => {
    setIsFiltering(true);
    const timeout = setTimeout(() => {
      setDebouncedFilter(filter);
      setIsFiltering(false);
    }, 500);
    return () => clearTimeout(timeout);
  }, [filter]);

  const fetchFunction = useMemo(
    () => (perPage, offset) => {
      if (debouncedFilter && debouncedFilter.trim() !== "") {
        return dispatch(fetchBuscarSensor(perPage, offset, debouncedFilter));
      } else {
        return dispatch(fetchSensor(perPage, offset));
      }
    },
    [debouncedFilter, dispatch]
  );

  const { dataSource, total, handlePage, page, isLoading, refreshData } = usePaginationMUI(
    fetchFunction,
    "sensor",
    0,
    perPage,
    debouncedFilter
  );

  const pageCount = Math.ceil(total / perPage);

  const fetchIdSensor = async (sensor) => {
    try {
      const result = await dispatch(fetchSensorById(sensor._id));
      if (result.payload?.data) {
        const sensorData = result.payload.data;
        setSensorActual(sensorData);
        console.log("Datos del sensor:", sensorData);
      } else {
        Swal.fire({
          icon: "warning",
          title: "Procesamiento invalido",
          text: "No se pudo obtener la data del sensor",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al obtener el sensor. |" + error.message,
      });
    }
  };
  // Función para manejar el cambio de estado del sensor
  const handleCambiarEstadoSensor = async (sensorId, nuevoEstado) => {
    try {
      await dispatch(cambiarEstadoSensor(sensorId, nuevoEstado));
      // Refrescar datos después del cambio de estado exitoso
      refreshData();
    } catch (error) {
      console.error('Error al cambiar estado del sensor:', error);
      // Re-lanzar el error para que el DeviceControlButton lo maneje
      throw error;
    }
  };

  return (
    <>
      <Box px={2} py={3} sx={{ width: '100%' }}>
        <Box mb={3} display="flex" flexDirection={isMobile ? "column" : "row"} gap={2} justifyContent="center" alignItems="center">
          <TextField
            size="small"
            placeholder="🔍 Filtrar por nombre o tipo"
            onChange={(e) => setFilter(e.target.value)}
            fullWidth
            sx={{ maxWidth: 600 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Por página</InputLabel>
            <Select
              value={perPage}
              label="Por página"
              onChange={(e) => setPerPage(e.target.value)}
            >
              {perPageOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {isLoading || isFiltering ? (
          <Box textAlign="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ width: '100%', px: 1 }}>
              <Box
                display="grid"
                gridTemplateColumns={{
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                  lg: "repeat(3, 1fr)",
                  xl: "repeat(3, 1fr)",
                }}
                gridAutoRows="1fr" // Esta es la clave: hace que todas las filas tengan la misma altura
                gap={2}
                width="100%"
              >
                {dataSource.map((item) => {
                  const parsed = parserSensores(item);
                  return (
                    <Card
                      key={item._id}
                      sx={{
                        height: "100%", // Vuelve a altura dinámica pero ocupa toda la celda del grid
                        backgroundColor: "#303C54",
                        color: "primary.contrastText",
                        borderRadius: 2,
                        boxShadow: 4,
                        display: "flex",
                        flexDirection: "column",
                        "&:hover": { boxShadow: 8 },
                      }}
                    >
                      <CardHeader
                        title={item.nombre}
                        action={
                          <>
                            {item.alwaysOn && (
                              <Tooltip title="Dispositivo Crítico">
                                <IconButton sx={{ color: "orange" }}>
                                  <SecurityIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {permissions.puedeControlar && (
                              <DeviceControlButton
                                dispositivo={item}
                                tipo="sensor"
                                onCambiarEstado={handleCambiarEstadoSensor}
                              />
                            )}
                            {permissions.puedeActualizar && (
                              <IconButton
                                onClick={() => navigate(`/sensores/editar/${item._id}`)}
                                sx={{ color: "primary.contrastText" }}
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            <IconButton
                              onClick={() => {
                                setOpenDialog(true);
                                fetchIdSensor(item);
                              }}
                              sx={{ color: "primary.contrastText" }}
                            >
                              <InfoOutlinedIcon />
                            </IconButton>
                          </>
                        }
                        sx={{
                          pb: 0,
                          "& .MuiCardHeader-title": {
                            typography: "h6",
                            fontSize: "1rem",
                            lineHeight: 1.3,
                            // Removemos las restricciones de líneas para permitir altura natural
                          },
                        }}
                      />
                      <CardContent sx={{
                        flexGrow: 1,
                        pt: 1,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start", // Cambio de space-between a flex-start
                      }}>
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="stretch"
                          gap={{ xs: 0.5, sm: 1 }}
                          flexWrap="nowrap"
                          sx={{
                            flexDirection: { xs: "column", sm: "row" }
                          }}
                        >
                          {[
                            { label: parsed.tipo, value: parsed.unidades },
                            {
                              label: "Rango Mínimo",
                              value: parsed.rangoMin !== undefined ? parsed.rangoMin : (item.rangoMin !== undefined ? item.rangoMin : '-')
                            },
                            {
                              label: "Rango Máximo",
                              value: parsed.rangoMax !== undefined ? parsed.rangoMax : (item.rangoMax !== undefined ? item.rangoMax : '-')
                            },
                          ].map(({ label, value }) => {
                            const isRange = label === "Rango Mínimo" || label === "Rango Máximo";
                            const showTooltip = isRange && value !== '-' && value !== '';
                            const displayValue = isRange ? formatNumberAbbr(value) : value;
                            return (
                              <Box
                                key={label}
                                sx={{
                                  backgroundColor: "#1F2A38",
                                  borderRadius: 1,
                                  p: { xs: 1.5, sm: 2 },
                                  textAlign: "center",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  minWidth: { xs: "100%", sm: 100 },
                                  minHeight: { xs: 70, sm: 120 }, // Cambio a minHeight para permitir crecimiento
                                  flex: { xs: "none", sm: 1 },
                                  mb: { xs: 0.5, sm: 0 }
                                }}
                              >
                                {iconMap[label] || <BoltIcon fontSize={isMobile ? "medium" : "large"} />}
                                <Typography 
                                  variant="caption" 
                                  mt={0.5}
                                  sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
                                >
                                  {label}
                                </Typography>
                                {showTooltip ? (
                                  <Tooltip title={value.toString()} placement="top">
                                    <Typography
                                      variant={isMobile ? "body2" : "h6"}
                                      color="white"
                                      sx={{
                                        fontWeight: "bold",
                                        fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" },
                                        maxWidth: 110,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {displayValue}
                                    </Typography>
                                  </Tooltip>
                                ) : (
                                  <Typography
                                    variant={isMobile ? "body2" : "h6"}
                                    color="white"
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.1rem" }
                                    }}
                                  >
                                    {displayValue}
                                  </Typography>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            </Box>

            {dataSource.length > 0 && (
              <Box mt={4} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(_e, v) => handlePage(v)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <DialogCustom
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        title={
          "Detalles del Sensor - " + (sensorActual ? sensorActual.nombre : "")
        }
        maxWidth="md"
      >
        <InfoElemento sensor={sensorActual} isLoading={isLoading} />
      </DialogCustom>
    </>
  );
};

export default Sensor;
